class CustomDropdown {
  static instances = [];

  constructor(dropdownElement) {
    this.dropdown = dropdownElement;
    this.toggle = dropdownElement.querySelector('.dropdown-toggle');
    this.text = dropdownElement.querySelector('.dropdown-text');
    this.list = dropdownElement.querySelector('.dropdown-list');
    this.items = [...dropdownElement.querySelectorAll('.dropdown-list-item')];
    this.hiddenInput = dropdownElement.querySelector('[data-option-input]');

    this.placeholder = this.toggle.dataset.placeholder || this.text.textContent.trim();

    this.productRoot = this.dropdown.closest('[data-product-root]');
    this.form = this.productRoot?.querySelector('form[action*="/cart/add"]');
    this.variantInput = this.form?.querySelector('[data-variant-id]') || this.form?.querySelector('[name="id"]');
    this.sizeImage = this.productRoot?.querySelector('[data-size-image]');
    this.productDataElement = this.productRoot?.querySelector('[data-product-json]');

    this.productData = this.readProductData();

    CustomDropdown.instances.push(this);

    this.init();
  }

  readProductData() {
    if (!this.productDataElement) return null;

    try {
      return JSON.parse(this.productDataElement.textContent);
    } catch (error) {
      console.error('Не удалось прочитать JSON товара.', error);
      return null;
    }
  }

  init() {
    if (!this.toggle || !this.text || !this.list || !this.hiddenInput) return;

    this.syncFromHiddenInput();

    this.toggle.addEventListener('click', (event) => {
      event.stopPropagation();

      if (this.isUnavailable()) return;

      const willBeOpen = !this.dropdown.classList.contains('open');

      if (willBeOpen) {
        CustomDropdown.instances.forEach((instance) => {
          if (instance !== this) instance.close();
        });
      }

      this.dropdown.classList.toggle('open');
      this.toggle.setAttribute('aria-expanded', String(this.dropdown.classList.contains('open')));
    });

    this.items.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.stopPropagation();

        if (
          item.classList.contains('disabled') ||
          this.dropdown.classList.contains('disabled') ||
          this.dropdown.classList.contains('read-only')
        ) {
          return;
        }

        this.select(item);
      });
    });

    document.addEventListener('click', (event) => {
      if (!this.dropdown.contains(event.target)) this.close();
    });
  }

  isUnavailable() {
    return this.dropdown.classList.contains('disabled') || this.dropdown.classList.contains('read-only');
  }

  syncFromHiddenInput() {
    const value = String(this.hiddenInput.value || '').trim();

    const selectedItem = this.items.find(
      (item) => String(item.dataset.optionValue || item.dataset.value || '').trim() === value,
    );

    if (selectedItem) {
      this.items.forEach((item) => {
        item.classList.toggle('selected', item === selectedItem);
        item.setAttribute('aria-selected', String(item === selectedItem));
      });

      this.text.textContent = selectedItem.dataset.text || selectedItem.textContent.trim();

      this.toggle.classList.remove('empty');
    } else {
      this.resetDisplay();
    }
  }

  select(item) {
    const value = String(item.dataset.optionValue || item.dataset.value || '').trim();

    this.items.forEach((currentItem) => {
      const selected = currentItem === item;
      currentItem.classList.toggle('selected', selected);
      currentItem.setAttribute('aria-selected', String(selected));
    });

    this.text.textContent = item.dataset.text || item.textContent.trim();
    this.hiddenInput.value = value;
    this.toggle.classList.remove('empty');

    this.close();

    this.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    // После обновления значения этой опции сопоставляем комбинацию
    // выбранных опций с вариантом товара.
    this.updateProductVariant();
  }

  updateProductVariant() {
    if (!this.productData || !this.productRoot || !this.variantInput) return;

    const optionInputs = [...this.productRoot.querySelectorAll('[data-option-input]')];

    const selectedValues = optionInputs.map((input) => String(input.value || '').trim());

    // Пока не выбраны все опции — соответствующий вариант ещё не определён.
    if (selectedValues.some((value) => value === '')) return;

    const variant = this.productData.variants.find((candidate) =>
      candidate.options.every((optionValue, index) => optionValue === selectedValues[index]),
    );

    if (!variant) {
      this.variantInput.value = '';
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    this.variantInput.value = variant.id;
    this.variantInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

    const sizeIndex = this.productData.options.findIndex((option) => {
      const name = option.trim().toLowerCase();
      return name === 'size' || name === 'размер';
    });

    if (this.sizeImage && sizeIndex !== -1) {
      this.sizeImage.dataset.size = variant.options[sizeIndex] || '';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  resetDisplay() {
    this.items.forEach((item) => {
      item.classList.remove('selected');
      item.setAttribute('aria-selected', 'false');
    });

    this.text.textContent = this.placeholder;
    this.hiddenInput.value = '';
    this.toggle.classList.add('empty');
  }

  close() {
    this.dropdown.classList.remove('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  open() {
    if (this.isUnavailable()) return;

    CustomDropdown.instances.forEach((instance) => {
      if (instance !== this) instance.close();
    });

    this.dropdown.classList.add('open');
    this.toggle.setAttribute('aria-expanded', 'true');
  }

  setValue(value) {
    const normalizedValue = String(value || '').trim();

    const item = this.items.find(
      (currentItem) =>
        String(currentItem.dataset.optionValue || currentItem.dataset.value || '').trim() === normalizedValue,
    );

    if (item) {
      this.select(item);
    } else {
      this.reset();
    }
  }

  getValue() {
    return this.hiddenInput.value;
  }

  reset() {
    this.resetDisplay();
    this.close();

    this.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    this.updateProductVariant();
  }

  setDisabled(disabled) {
    this.dropdown.classList.toggle('disabled', disabled);
    this.toggle.disabled = disabled;

    if (disabled) this.close();
  }

  setReadOnly(readOnly) {
    this.dropdown.classList.toggle('read-only', readOnly);

    if (readOnly) this.close();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.dropdown').forEach((dropdownElement) => {
    new CustomDropdown(dropdownElement);
  });
});
